import { defineStore } from 'pinia'
import { ref } from 'vue'
import { dataGet, dataSet, getNested } from '@landingpage/Common/utils'
import { createBackendAdapter } from '@landingpage/Registration/composables/useBackendAdapter'
import { useFieldPathResolver } from './useFieldPathResolver'
import i18n from '@landingpage/Common/plugins/i18n'

const { t } = i18n.global

export const useCheckoutFormStore = defineStore('checkoutForm', () => {
  const { getFieldPath } = useFieldPathResolver()
  // State
  const formData = ref({
    contact: {},
    extended_fields: {},
    booking: { extended_fields: {} },
  })
  const initialData = ref({
    contact: {},
    extended_fields: {},
    booking: { extended_fields: {} },
  })
  const errors = ref({})
  const loading = ref(false)

  const initialize = (data, fields = null) => {
    // Initialize with empty structure
    formData.value = {
      contact: {},
      extended_fields: {},
      booking: { extended_fields: {} },
    }

    // If fields provided, structure data based on field models
    if (fields?.items) {
      fields.items.forEach((field) => {
        const path = getFieldPath(field)
        if (path) {
          const value = getNested(data, path) ?? null
          dataSet(formData.value, path, value)
        }
      })
    } else {
      // Fallback: assume all data is contact data
      formData.value.contact = { ...data }
    }

    // Include guest_group_id for visibility rule evaluation
    if (data?.guest_group_id) {
      formData.value.guest_group_id = data.guest_group_id
    }

    // Store initial state
    initialData.value = JSON.parse(JSON.stringify(formData.value))
    errors.value = {}
  }

  const updateField = (path, value) => dataSet(formData.value, path, value)

  const getData = (path) => {
    return dataGet(formData.value, path, '')
  }

  const getInitialData = (path) => {
    return dataGet(initialData.value, path, null)
  }
 
  const getErrors = () => {
    return errors.value
  }

  const addError = (path, message) => {
    if (!errors.value[path]) {
      errors.value[path] = []
    }
    errors.value[path].push(message)
  }

  const clearErrors = () => errors.value = {}

  /**
   * Validate form fields
   * @param {Array} fields - Field definitions to validate
   * @returns {boolean} - Whether form is valid
   */
  const validate = (fields) => {
    clearErrors()

    fields.forEach((item) => {
      if (item.required && item.field) {
        const path = getFieldPath(item)
        const value = getData(path)
        if (
          [null, '', [], false].includes(value) ||
          (Array.isArray(value) && value.length === 0)
        ) {
          addError(path, t('validation.required', { attribute: item.field.label }))
        }
      }
    })

    return Object.keys(errors.value).length === 0
  }

  const setErrors = (apiErrors) => {
    clearErrors()

    Object.keys(apiErrors).forEach((key) => {
      const messages = apiErrors[key]

      if (Array.isArray(messages)) {
        messages.forEach((message) => {
          addError(key, message)
        })
      }
    })
  }

  const reset = () => {
    formData.value = {
      contact: {},
      extended_fields: {},
      booking: { extended_fields: {} },
    }
    initialData.value = {
      contact: {},
      extended_fields: {},
      booking: { extended_fields: {} },
    }
    errors.value = {}
    loading.value = false
  }

  const createAdapter = () => {
    return createBackendAdapter({
      getData,
      getInitialData,
      getErrors,
      updateField,
      addError,
      clearErrors,
    })
  }

  return {
    formData,
    initialData,
    errors,
    loading,

    initialize,
    updateField,
    getData,
    getInitialData,
    getErrors,
    addError,
    clearErrors,
    validate,
    setErrors,
    reset,
    createAdapter,
    getFieldPath,
  }
})
